import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  Channel,
  ChannelSchema,
  TransportStream,
} from '@/lib/schema/channelGuide';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

type ChannelFormProps = {
  channels: Channel[];
  channel?: Channel;
  onSubmit: (data: any) => void;
  mutation: any;
};

export function ChannelForm({
  channels,
  channel,
  onSubmit,
  mutation,
}: ChannelFormProps) {
  const navigate = useNavigate();

  const form = useForm<Channel>({
    resolver: zodResolver(ChannelSchema),
    defaultValues: {
      id: channel?.id || uuidv4(),
      name: channel?.name || '',
      ip: channel?.ip || '',
      pnum: channel?.pnum,
      port: channel?.port,
      activated: channel?.activated || true,
      transportStream: channel?.transportStream || TransportStream.UDP,
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((value) => {
          if (channels?.some((v) => v.ip === value.ip && v.id !== value.id)) {
            form.setError('ip', {
              type: 'manual',
              message: 'IP address must be unique',
            });
            return;
          }
          if (
            channels?.some((v) => v.pnum === value.pnum && v.id !== value.id)
          ) {
            form.setError('pnum', {
              type: 'manual',
              message: 'Channel number must be unique',
            });
            return;
          }
          onSubmit(value);
        })}
        className="space-y-6"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  inError={!!form.formState.errors.name}
                  placeholder="Enter controller name"
                  {...field}
                />
              </FormControl>
              <FormMessage>{fieldState.error?.message || ' '}</FormMessage>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="pnum"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Channel number</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  inError={!!form.formState.errors.pnum}
                  placeholder="Enter channel number"
                  {...field}
                />
              </FormControl>
              <FormMessage>{fieldState.error?.message || ' '}</FormMessage>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="ip"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Ip</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  inError={!!form.formState.errors.ip}
                  placeholder="Enter IP address"
                  {...field}
                />
              </FormControl>
              <FormMessage>{fieldState.error?.message || ' '}</FormMessage>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="port"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Port</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  inError={!!form.formState.errors.port}
                  placeholder="Enter port"
                  {...field}
                />
              </FormControl>
              <FormMessage>{fieldState.error?.message || ' '}</FormMessage>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="transportStream"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Transport stream</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger isError={!!fieldState.error}>
                    <SelectValue placeholder="Select Transport stream" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(TransportStream).map((brand) => (
                    <SelectItem key={brand} value={brand}>
                      {brand}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage>{fieldState.error?.message || ''}</FormMessage>
            </FormItem>
          )}
        />

        <div className="flex gap-4 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {channel
              ? mutation.isPending
                ? 'Updating...'
                : 'Update channel'
              : mutation.isPending
                ? 'Creating...'
                : 'Create channel'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
